/**
 * Start Time Settings
 *
 * Kalshi reports game start times that are typically ~10 minutes early.
 * This setting allows configuring an offset to adjust the displayed/used start time.
 */

import { storageKey } from '@/lib/infra/storage';
import type { Sport } from '@/lib/sportsDiscovery';

const STORAGE_KEY = storageKey('nba-start-time-offset-minutes');
const DEFAULT_OFFSET_MINUTES = 10;

/**
 * Sport-specific buffer minutes added to Kalshi's reported start time.
 * Kalshi times are typically early; the buffer corrects for each sport's
 * actual tipoff/puck-drop delay.
 */
const SPORT_BUFFER_MINUTES: Partial<Record<Sport, number>> = {
  nba: 10,
  nhl: 7,
};

/** Get sport-specific buffer minutes. Falls back to 0 for unlisted sports. */
export function getSportBufferMinutes(sport?: Sport | null): number {
  if (!sport) return DEFAULT_OFFSET_MINUTES;
  return SPORT_BUFFER_MINUTES[sport] ?? 0;
}

/**
 * Get the configured start time offset in minutes.
 * Positive values shift the start time later (e.g., +9 means add 9 minutes).
 */
export function getStartTimeOffsetMinutes(): number {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return DEFAULT_OFFSET_MINUTES;

  const parsed = parseInt(stored, 10);
  if (!Number.isFinite(parsed)) return DEFAULT_OFFSET_MINUTES;

  return parsed;
}

/**
 * Set the start time offset in minutes.
 */
export function setStartTimeOffsetMinutes(minutes: number): void {
  localStorage.setItem(STORAGE_KEY, String(minutes));
}

/**
 * Apply the offset to an ISO timestamp string.
 * Returns a new ISO timestamp with the offset applied.
 */
export function applyStartTimeOffset(isoTimestamp: string): string {
  const offset = getStartTimeOffsetMinutes();
  if (offset === 0) return isoTimestamp;

  const date = new Date(isoTimestamp);
  if (!Number.isFinite(date.getTime())) return isoTimestamp;

  date.setMinutes(date.getMinutes() + offset);
  return date.toISOString();
}
